// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * Proactive Token Refresh Hook
 *
 * Monitors authentication state and proactively refreshes the BFF session
 * before it expires. This prevents users from encountering 401 errors
 * during normal usage.
 *
 * Strategy:
 * 1. On mount, start monitoring with a periodic check interval
 * 2. Call lightweight /api/auth/refresh endpoint at regular intervals (default: 4 minutes)
 * 3. If refresh fails, the user will be redirected to login
 *    on their next API call (handled by Axios interceptor)
 *
 * Note: This uses a lightweight API call (~100 bytes) instead of the previous
 * iframe-based approach which loaded the full React app (~500KB+).
 */

import { useEffect, useRef, useCallback } from 'react';

/** Default interval between refresh attempts (ms) - 45 seconds
 * Keycloak access tokens have a short TTL (~60s). The BFF now auto-refreshes
 * tokens server-side, but this proactive check ensures the session stays warm
 * and provides early warning of session expiry.
 */
const DEFAULT_REFRESH_INTERVAL = 45 * 1000;

/** Minimum interval between refresh attempts (ms) - 30 seconds */
const MIN_REFRESH_INTERVAL = 30 * 1000;

/** Activity threshold - only refresh if user was active within this time (ms) - 10 minutes */
const ACTIVITY_THRESHOLD = 10 * 60 * 1000;

interface UseTokenRefreshOptions {
  /** Interval between refresh attempts in milliseconds */
  refreshInterval?: number;
  /** Whether to enable proactive refresh */
  enabled?: boolean;
  /** Callback when refresh succeeds */
  onRefreshSuccess?: () => void;
  /** Callback when refresh fails */
  onRefreshFail?: (error: string) => void;
}

/**
 * Hook to enable proactive token refresh for authenticated sessions.
 * Should be used in the main app layout or authenticated wrapper component.
 */
export const useTokenRefresh = (options: UseTokenRefreshOptions = {}) => {
  const {
    refreshInterval = DEFAULT_REFRESH_INTERVAL,
    enabled = true,
    onRefreshSuccess,
    onRefreshFail,
  } = options;

  const lastActivityRef = useRef<number>(Date.now());
  const refreshInProgressRef = useRef<boolean>(false);
  // React 19's `useRef` requires an explicit initial value; the type
  // includes `| undefined` so we can clear it in the cleanup callback.
  const intervalRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

  // Track user activity
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // Check if user has been active recently
  const isUserActive = useCallback(() => {
    const timeSinceActivity = Date.now() - lastActivityRef.current;
    return timeSinceActivity < ACTIVITY_THRESHOLD;
  }, []);

  // Perform the refresh using lightweight API endpoint
  const performRefresh = useCallback(async () => {
    // Skip if already refreshing
    if (refreshInProgressRef.current) {
      console.log('[TokenRefresh] Skip - refresh already in progress');
      return;
    }

    // Skip if user hasn't been active
    if (!isUserActive()) {
      console.log('[TokenRefresh] Skip - user inactive');
      return;
    }

    // Skip if we're in an iframe (prevent infinite nesting)
    if (globalThis.self !== globalThis.top) {
      console.log('[TokenRefresh] Skip - running in iframe');
      return;
    }

    refreshInProgressRef.current = true;
    console.log('[TokenRefresh] Starting lightweight refresh...');

    try {
      // Call lightweight refresh endpoint (~100 bytes vs ~500KB+ iframe approach)
      const response = await fetch('/api/auth/refresh', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log('[TokenRefresh] Session valid, expires in:', data.expiresIn, 'seconds');
        onRefreshSuccess?.();
      } else if (response.status === 401) {
        console.log('[TokenRefresh] Session expired, user will be redirected on next API call');
        // Don't call onRefreshFail here - let the Axios interceptor handle
        // the redirect on the next API call
        onRefreshFail?.('Session expired');
      } else {
        console.log('[TokenRefresh] Unexpected response:', response.status);
        onRefreshFail?.(`Unexpected status: ${response.status}`);
      }
    } catch (error) {
      console.error('[TokenRefresh] Error during refresh:', error);
      onRefreshFail?.(error instanceof Error ? error.message : 'Unknown error');
    } finally {
      refreshInProgressRef.current = false;
    }
  }, [isUserActive, onRefreshSuccess, onRefreshFail]);

  // Set up activity tracking
  useEffect(() => {
    if (!enabled) return;

    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];

    events.forEach((event) => {
      globalThis.addEventListener(event, updateActivity, { passive: true });
    });

    return () => {
      events.forEach((event) => {
        globalThis.removeEventListener(event, updateActivity);
      });
    };
  }, [enabled, updateActivity]);

  // Set up refresh interval
  useEffect(() => {
    if (!enabled) return;

    // Validate interval
    const actualInterval = Math.max(refreshInterval, MIN_REFRESH_INTERVAL);

    console.log(`[TokenRefresh] Starting proactive refresh with interval: ${actualInterval}ms`);

    // Initial activity update
    updateActivity();

    // Start the interval
    intervalRef.current = setInterval(performRefresh, actualInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = undefined;
      }
    };
  }, [enabled, refreshInterval, performRefresh, updateActivity]);

  // Expose manual refresh trigger
  return {
    /** Manually trigger a refresh attempt */
    triggerRefresh: performRefresh,
    /** Update the last activity timestamp */
    updateActivity,
  };
};

export default useTokenRefresh;
